import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageCircle, Trash2, Store } from 'lucide-react'
import { useAuthStore } from '@/store/auth.store'
import { useComments, useAddComment, useDeleteComment } from '@/hooks/useComments'
import { timeAgo } from '@/lib/time'

const C = {
  teal: '#0F5C5C',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
  error: '#C85C5C',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

interface Props {
  postId: string
  allowComments: boolean
}

export default function CommentsSection({ postId, allowComments }: Props) {
  const navigate = useNavigate()
  const { user, profile } = useAuthStore()
  const [limit, setLimit] = useState(50)
  const { data, isLoading } = useComments(postId, limit)
  const addComment = useAddComment(postId)
  const deleteComment = useDeleteComment(postId)
  const [text, setText] = useState('')

  const comments = data?.data ?? []
  const total = data?.count ?? comments.length

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = text.trim()
    if (!trimmed) return
    addComment.mutate(trimmed, { onSuccess: () => setText('') })
  }

  return (
    <div style={{ marginTop: 28 }}>
      <div
        style={{
          fontFamily: FH,
          fontWeight: 700,
          fontSize: 16,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <MessageCircle size={18} color={C.ink2} />
        Comentarios {total > 0 && `(${total})`}
      </div>

      {!allowComments ? (
        <div
          style={{
            fontSize: 13.5,
            color: C.ink2,
            background: C.grayBg,
            borderRadius: 12,
            padding: '14px 16px',
          }}
        >
          Los comentarios están desactivados para esta publicación.
        </div>
      ) : (
        <>
          {user ? (
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Escribe un comentario…"
                maxLength={500}
                rows={2}
                style={{
                  flex: 1,
                  resize: 'none',
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  padding: '10px 14px',
                  fontFamily: FB,
                  fontSize: 14,
                  color: C.ink,
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={addComment.isPending || !text.trim()}
                style={{
                  alignSelf: 'flex-end',
                  padding: '10px 18px',
                  borderRadius: 11,
                  border: 'none',
                  background: C.teal,
                  color: '#fff',
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 13.5,
                  cursor: addComment.isPending || !text.trim() ? 'not-allowed' : 'pointer',
                  opacity: addComment.isPending || !text.trim() ? 0.6 : 1,
                }}
              >
                {addComment.isPending ? 'Enviando…' : 'Comentar'}
              </button>
            </form>
          ) : (
            <div
              style={{
                fontSize: 13.5,
                color: C.ink2,
                background: C.grayBg,
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              Inicia sesión para comentar.
              <button
                onClick={() => navigate('/login')}
                style={{
                  border: 'none',
                  background: C.teal,
                  color: '#fff',
                  borderRadius: 9,
                  padding: '7px 14px',
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 12.5,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                Ingresar
              </button>
            </div>
          )}

          {isLoading ? null : comments.length === 0 ? (
            <div style={{ fontSize: 13.5, color: C.ink2, padding: '8px 0' }}>
              Sé el primero en comentar.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {comments.map((cm) => {
                const canDelete = cm.user_id === user?.id || profile?.role === 'admin'
                return (
                  <div key={cm.id} style={{ display: 'flex', gap: 10 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 99,
                        background: cm.is_from_biz ? C.teal : C.grayBg,
                        color: cm.is_from_biz ? '#fff' : C.ink2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 14,
                        flexShrink: 0,
                      }}
                    >
                      {cm.is_from_biz ? (
                        <Store size={16} />
                      ) : (
                        (cm.author?.username ?? '?').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 13.5 }}>
                          {cm.author?.username ?? 'Usuario'}
                        </span>
                        {cm.is_from_biz && (
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: C.teal,
                              background: `${C.teal}18`,
                              borderRadius: 6,
                              padding: '2px 6px',
                            }}
                          >
                            NEGOCIO
                          </span>
                        )}
                        <span style={{ fontSize: 12, color: C.ink2 }}>
                          · {timeAgo(cm.created_at)}
                        </span>
                        {canDelete && (
                          <button
                            onClick={() => deleteComment.mutate(cm.id)}
                            disabled={deleteComment.isPending}
                            style={{
                              marginLeft: 'auto',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: 4,
                              color: C.error,
                              flexShrink: 0,
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: C.ink,
                          lineHeight: 1.5,
                          marginTop: 2,
                          wordBreak: 'break-word',
                        }}
                      >
                        {cm.text}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {comments.length < total && (
            <button
              onClick={() => setLimit((l) => l + 50)}
              style={{
                marginTop: 16,
                width: '100%',
                padding: '10px',
                borderRadius: 11,
                border: `1px solid ${C.border}`,
                background: '#fff',
                color: C.teal,
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 13.5,
                cursor: 'pointer',
              }}
            >
              Ver más comentarios ({total - comments.length})
            </button>
          )}
        </>
      )}
    </div>
  )
}
